import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Container,
  Grid,
  Box,
  Typography,
  Button,
  useMediaQuery,
} from "@mui/material";
import sh from "bundles/common/utils/sh";
import PublicJobOfferSummary from "bundles/common/components/PublicJobOfferSummary";
import RelatedJobOffers from "bundles/common/components/RelatedJobOffers";
import SquarehubLoader from "bundles/common/components/SquarehubLoader";
import NotFound from "icons/notFound.svg";
import { FormattedMessage } from "react-intl";
import { useTheme } from "@mui/styles";
import BreadcrumbsBar from "bundles/common/components/BreadcrumbsBar";

const PublicJobOffer = () => {
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const { state } = useLocation();
  const i18nLocale = useSelector((state) => state.i18nLocale);

  const params = useParams();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [jobOffer, setJobOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const fetchJobOffer = () => {
    setLoading(true);
    sh.get(`public_job_offers/${params.job_offer_id}`, {
      authenticity_token: authenticityToken,
    })
      .then((res) => {
        setJobOffer(res.data?.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setNotFound(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchJobOffer();
  }, []);

  const crumbs = React.useMemo(() => {
    const defaultTrail = [
      { label: "Home", path: `/${i18nLocale}`, fullReload: true },
      { label: "Job Offers", path: `/${i18nLocale}/job-offers` },
    ];

    const sourceTrail = state?.crumbs?.length ? state.crumbs : defaultTrail;
    const normalized = sourceTrail.map(({ label, path, fullReload }, index) => ({
      label,
      path,
      fullReload: index === 0 ? true : Boolean(fullReload),
    }));

    // Guarantee section crumb exists even when navigating directly to the job page.
    const hasJobOffersCrumb = normalized.some(
      (crumb) => crumb.path === `/${i18nLocale}/job-offers`
    );
    if (!hasJobOffersCrumb) {
      normalized.push({ label: "Job Offers", path: `/${i18nLocale}/job-offers` });
    }

    // Guarantee subsection crumbs exist (industry + first category) when data is available.
    const industrySlug = Array.isArray(jobOffer?.company_industries)
      ? jobOffer.company_industries[0]
      : null;
    const categorySlug = Array.isArray(jobOffer?.categories)
      ? jobOffer.categories[0]
      : null;

    if (industrySlug) {
      const industryPath = `/${i18nLocale}/job-offers/${industrySlug}`;
      if (!normalized.some((crumb) => crumb.path === industryPath)) {
        normalized.push({
          label: industrySlug.charAt(0).toUpperCase() + industrySlug.slice(1),
          path: industryPath,
        });
      }
    }

    if (industrySlug && categorySlug) {
      const categoryPath = `/${i18nLocale}/job-offers/${industrySlug}/${categorySlug}`;
      if (!normalized.some((crumb) => crumb.path === categoryPath)) {
        normalized.push({
          label: categorySlug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
          path: categoryPath,
        });
      }
    }

    if (!jobOffer) return normalized;

    return [...normalized, { label: jobOffer.job_title, current: true }];
  }, [state?.crumbs, i18nLocale, jobOffer]);

  return (
    <Box sx={{ backgroundColor: "#F1F1F1", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg" style={{ marginTop: "32px" }}>
        <BreadcrumbsBar crumbs={crumbs} />
      </Container>
      <Container maxWidth="lg">
        <Grid container style={{ marginTop: "32px", marginBottom: "32px" }}>
          <Grid item xs={12}>
            {loading && <SquarehubLoader size={72} centered />}
            {!loading && jobOffer && (
              <PublicJobOfferSummary
                jobOffer={jobOffer}
                updateJobOffer={setJobOffer}
              />
            )}
            {!loading && jobOffer && (
              <RelatedJobOffers jobOfferId={jobOffer.id} />
            )}
            {!loading && notFound && (
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  padding: "32px",
                }}
              >
                <Box
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    marginBottom: "64px",
                  }}
                >
                  <Box style={{ marginBottom: "16px", textAlign: "center" }}>
                    <Typography variant="body1bold">
                      <FormattedMessage id="public_job_page.not_found" />
                    </Typography>
                  </Box>
                  <Button
                    color="primaryContained"
                    variant="rounded"
                    href={`${window.location.origin}/${i18nLocale}/candidates/sign_up?redirectTo=%2Fcandidate%2Fjob_offers`}
                  >
                    <FormattedMessage id="public_job_page.not_found_cta" />
                  </Button>
                </Box>
                <img
                  src={NotFound}
                  style={{ width: isDesktop ? "600px" : "80%" }}
                />
              </Box>
            )}

          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default PublicJobOffer;
